import { HttpClient } from '@angular/common/http';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { ToastsService } from '@catch/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AppState } from '../../app.reducer';
import { CompanyUserRole } from '../../enums/company-user.role.enum';
import { TFAMethodTypes } from '../../enums/tfa-types';
import { TFAMethod } from '../../interfaces/tfa-method';
import { hasPermission } from '../../store/auth/auth.reducer';
import { loadUserAccount } from '../../store/user-account/user-account.actions';
import { selectUserAccount } from '../../store/user-account/user-account.reducer';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class TFAService {
  private _tfaMethods: BehaviorSubject<TFAMethod[]>;
  tfaMethods$: Observable<TFAMethod[]>;
  triggerTFAForAll: EventEmitter<void> = new EventEmitter<void>();
  hasMethods: boolean = false;

  private httpClient = inject(HttpClient);
  private toastsService = inject(ToastsService);
  private store = inject(Store<AppState>);

  constructor() {
    this._tfaMethods = new BehaviorSubject<TFAMethod[]>([]);
    this.tfaMethods$ = this._tfaMethods.asObservable();
  }

  getMethods() {
    const request = this.httpClient
      .get(`${environment.apiBaseUrl}/company/user/authentication-methods`)
      .pipe(shareReplay());
    request.pipe(untilDestroyed(this)).subscribe({
      next: (result: TFAMethod[]) => {
        this.hasMethods = result.length > 0;
        this._tfaMethods.next(result);
      },
    });
    return request;
  }

  getAppCredential(name: string): Observable<{ id: string; code: string; 'qr-code-image': string }> {
    return this.httpClient.post<{ id: string; code: string; 'qr-code-image': string }>(
      `${environment.apiBaseUrl}/company/user/2fa-credential`,
      { name }
    );
  }

  confirmAppCredential(id: string, code: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .post(`${environment.apiBaseUrl}/company/user/2fa-credential/${id}/confirm`, { code })
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (result: { name: string }) => {
            this._tfaMethods.next(
              this._tfaMethods.value.concat({
                id,
                name: result.name,
                created: DateTime.now().toISO(),
                lastUsed: DateTime.now().toISO(),
                type: TFAMethodTypes.APP,
              })
            );
            this.toastsService.success('company-tool.views.security.tfa.create-success');
            this.onTFAMethodCreated();
            resolve(result);
          },
          error: (error) => reject(error),
        });
    });
  }

  cancelAppCreation(id: string): void {
    if (id) {
      this.deleteMethod(
        { id, type: TFAMethodTypes.APP, created: DateTime.now().toISO(), lastUsed: DateTime.now().toISO(), name: '' },
        true
      );
    }
  }

  deleteMethod(method: TFAMethod, suppressToast: boolean = false) {
    const request = this.httpClient.delete(`${environment.apiBaseUrl}/company/user/credential/${method.id}`);
    request.pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this._tfaMethods.next(this._tfaMethods.value.filter((item) => item.id !== method.id));
        if (method.type === TFAMethodTypes.APP && !suppressToast) {
          this.toastsService.success('company-tool.views.security.tfa.delete-success');
        }
      },
    });
    return request;
  }

  toggleTFAForAll(require: boolean): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.httpClient
        .patch(`${environment.apiBaseUrl}/company/require-2fa`, { require })
        .pipe(shareReplay(), untilDestroyed(this))
        .subscribe({
          next: () => {
            this.toastsService.success(
              require
                ? 'company-tool.views.settings.company.users.sections.tfa.required-success'
                : 'company-tool.views.settings.company.users.sections.tfa.not-required-success'
            );
            this.store.dispatch(loadUserAccount());
            resolve(require);
          },
          error: (error) => reject(error),
        });
    });
  }

  onTFAMethodCreated(): void {
    this.store
      .select(selectUserAccount)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        if (user?.tfaRequiredSince) return;
        this.store
          .select(hasPermission(CompanyUserRole.ADMIN))
          .pipe(untilDestroyed(this))
          .subscribe((isAdmin) => {
            if (isAdmin) this.triggerTFAForAll.emit();
          });
      });
  }
}
